import type { ReactNode } from "react";
import {
  Tab,
  TabList,
  TabPanel,
  Tabs as AriaTabs,
} from "react-aria-components";
import { Icon, type IconKey } from "@core/assets";
import { cn } from "@core/utils";

type TabsVariant = "pill" | "phase";

interface TabItem {
  id: string;
  label: string;
  /** Number shown after the label (`pill` variant): « Tous 1 ». */
  count?: number;
  /** Second line under the label (`phase` variant): « En préparation », « Verrouillée ». */
  caption?: string;
  /**
   * Leading icon. `phase`: a disabled tab always shows the lock. `pill`: shown only when given
   * (the appel's five steps: a tick for a finished step, a dashed circle for one in progress, a
   * padlock for one not available yet); the team filters give none.
   */
  icon?: IconKey;
  /** Not selectable; in the `phase` variant this is the « Verrouillée » state. */
  isDisabled?: boolean;
}

interface TabsProps {
  items: readonly TabItem[];
  /** Selected tab id (controlled). */
  value: string;
  onChange: (id: string) => void;
  /**
   * `pill`: rounded tabs with a count, the selected one outlined: team list, invitations drawer.
   * `phase`: underlined tabs with an icon and two lines: the cohort's six phases.
   */
  variant?: TabsVariant;
  "aria-label": string;
  /** Content of the selected tab. Omit when the screen filters its own list below the strip. */
  children?: ReactNode;
  className?: string;
}

const TAB_BASE =
  "cursor-pointer outline-none transition-colors " +
  "focus-visible:ring-2 focus-visible:ring-focus-ring " +
  "disabled:cursor-not-allowed";

const PILL_TAB =
  "inline-flex items-center gap-xs rounded-full border border-transparent px-sm py-1.5 " +
  "text-sm-medium text-(--text-secondary) hover:text-(--text-primary) disabled:text-(--text-disabled) " +
  "selected:border-(--border-default) selected:bg-surface-card selected:text-(--text-primary)";

const PHASE_TAB =
  "flex min-w-40 shrink-0 items-center lg:min-w-0 lg:flex-1 lg:shrink gap-xs border-b-2 border-(--border-default) px-sm py-sm " +
  "text-start text-(--text-secondary) hover:text-(--text-primary) disabled:text-(--text-disabled) " +
  "selected:border-(--text-primary) selected:text-(--text-primary)";

/**
 * Tab strip on React Aria's tabs: arrow-key navigation, roving focus and the tab/tabpanel
 * wiring come from the library. Controlled, because the screens filter an already-fetched
 * list by the selected id rather than swapping a panel per tab.
 *
 * One primitive, two looks (Figma, workflows 1 and 3). It replaces the four hand-written tab
 * strips of the old modules; those are left alone until their screens are rebuilt.
 */
export function Tabs({
  items,
  value,
  onChange,
  variant = "pill",
  children,
  className,
  ...props
}: TabsProps) {
  return (
    <AriaTabs
      selectedKey={value}
      onSelectionChange={(key) => onChange(String(key))}
      className={className}
    >
      <TabList
        aria-label={props["aria-label"]}
        className={cn(
          variant === "pill"
            ? "flex flex-wrap items-center gap-xxs"
            : "flex w-full items-stretch overflow-x-auto overflow-y-hidden"
        )}
      >
        {items.map((item) => (
          <Tab
            key={item.id}
            id={item.id}
            isDisabled={item.isDisabled}
            className={cn(TAB_BASE, variant === "pill" ? PILL_TAB : PHASE_TAB)}
          >
            {variant === "pill" ? (
              <>
                {item.icon ? (
                  <Icon name={item.icon} size={16} className="shrink-0" />
                ) : null}
                <span>{item.label}</span>
                {item.count !== undefined ? (
                  <span className="text-sm-regular text-(--text-tertiary)">
                    {item.count}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <Icon
                  name={
                    item.isDisabled ? "lock" : (item.icon ?? "circle-dashed")
                  }
                  size={20}
                  className="shrink-0"
                />
                <span className="flex min-w-0 flex-col">
                  <span className="truncate text-sm-semibold">
                    {item.label}
                  </span>
                  {item.caption ? (
                    <span
                      className={cn(
                        "truncate text-xs-regular",
                        item.isDisabled
                          ? "text-(--text-tertiary)"
                          : "text-(--status-open-text)"
                      )}
                    >
                      {item.caption}
                    </span>
                  ) : null}
                </span>
              </>
            )}
          </Tab>
        ))}
      </TabList>
      {children ? (
        <TabPanel id={value} className="outline-none">
          {children}
        </TabPanel>
      ) : null}
    </AriaTabs>
  );
}

export type { TabItem, TabsProps, TabsVariant };
